import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pdf } from '@react-pdf/renderer';
import api from '../services/api';
import CameraCapture from '../components/CameraCapture';
import { OrcamentoPDF } from '../components/OrcamentoPDF';

const STATUS_LABELS: Record<string, string> = {
  RASCUNHO: 'Rascunho', ENVIADO: 'Enviado', APROVADO: 'Aprovado',
  REJEITADO: 'Rejeitado', CANCELADO: 'Cancelado',
};

const STATUS_OPTIONS = ['RASCUNHO', 'ENVIADO', 'APROVADO', 'REJEITADO', 'CANCELADO'];

const TIPO_INSTALACAO = [
  { value: 'PAREDE', label: 'Parede' },
  { value: 'TETO', label: 'Teto' },
  { value: 'ENTRE_VAOS', label: 'Entre vãos' },
];

const ACIONAMENTO = [
  { value: 'MANUAL_CORDA', label: 'Manual (Corda)' },
  { value: 'MANUAL_CORRENTE', label: 'Manual (Corrente)' },
  { value: 'MOTORIZADO', label: 'Motorizado' },
  { value: 'AUTOMATICO', label: 'Automático' },
];

const LADO_COMANDO = [
  { value: 'DIREITA', label: 'Direita' },
  { value: 'ESQUERDA', label: 'Esquerda' },
];

export default function OrcamentoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraForItemId, setCameraForItemId] = useState<string | null>(null);
  const [uploadingFoto, setUploadingFoto] = useState<string | null>(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [showPdfMenu, setShowPdfMenu] = useState(false);
  const [editingInfo, setEditingInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({ dataVisita: '', dataValidade: '', observacoesGerais: '' });

  const [itemForm, setItemForm] = useState({
    ambiente: '',
    descricaoJanela: '',
    larguraCm: '',
    alturaCm: '',
    quantidade: '1',
    produtoId: '',
    tipoInstalacao: 'PAREDE',
    acionamento: 'MANUAL_CORDA',
    precoM2: '',
    precoInstalacao: '',
    bando: 'nao',
    ladoComando: 'DIREITA',
    observacoes: '',
  });

  const { data: orcamento, isLoading } = useQuery({
    queryKey: ['orcamento', id],
    queryFn: () => api.get(`/orcamentos/${id}`).then(r => r.data),
  });

  const { data: produtos = [] } = useQuery({
    queryKey: ['produtos-ativos'],
    queryFn: () => api.get('/produtos', { params: { ativo: 'true' } }).then(r => r.data),
  });

  const updateStatusMutation = useMutation({
    mutationFn: (status: string) => api.put(`/orcamentos/${id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orcamento', id] }),
  });

  const updateOrcamentoMutation = useMutation({
    mutationFn: (data: any) => api.put(`/orcamentos/${id}`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['orcamento', id] }); setEditingInfo(false); },
  });

  const deleteOrcamentoMutation = useMutation({
    mutationFn: () => api.delete(`/orcamentos/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orcamentos'] });
      navigate('/orcamentos');
    },
  });

  function handleDeleteOrcamento() {
    if (window.confirm(`Excluir o orçamento ${orcamento.numero}? Essa ação não pode ser desfeita.`)) {
      deleteOrcamentoMutation.mutate();
    }
  }

  const addItemMutation = useMutation({
    mutationFn: (data: any) => api.post(`/orcamentos/${id}/itens`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orcamento', id] });
      setShowItemForm(false);
      resetItemForm();
    },
  });

  const updateItemMutation = useMutation({
    mutationFn: ({ itemId, data }: any) => api.put(`/orcamentos/${id}/itens/${itemId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orcamento', id] });
      setEditingItemId(null);
      resetItemForm();
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: (itemId: string) => api.delete(`/orcamentos/${id}/itens/${itemId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orcamento', id] }),
  });

  const deleteFotoMutation = useMutation({
    mutationFn: (fotoId: string) => api.delete(`/orcamentos/itens/fotos/${fotoId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orcamento', id] }),
  });

  async function handleExportPdf(mode: 'interno' | 'cliente') {
    setShowPdfMenu(false);
    setExportingPdf(true);
    try {
      const blob = await pdf(
        <OrcamentoPDF orcamento={orcamento} apiBase={window.location.origin} mode={mode} />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${orcamento.numero}${mode === 'cliente' ? '-cliente' : ''}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExportingPdf(false);
    }
  }

  function startEditInfo() {
    setInfoForm({
      dataVisita: orcamento.dataVisita ? new Date(orcamento.dataVisita).toISOString().slice(0, 16) : '',
      dataValidade: orcamento.dataValidade ? new Date(orcamento.dataValidade).toISOString().slice(0, 10) : '',
      observacoesGerais: orcamento.observacoesGerais || '',
    });
    setEditingInfo(true);
  }

  function resetItemForm() {
    setItemForm({ ambiente: '', descricaoJanela: '', larguraCm: '', alturaCm: '', quantidade: '1', produtoId: '', tipoInstalacao: 'PAREDE', acionamento: 'MANUAL_CORDA', precoM2: '', precoInstalacao: '', bando: 'nao', ladoComando: 'DIREITA', observacoes: '' });
  }

  function handleProdutoChange(produtoId: string) {
    const prod = produtos.find((p: any) => p.id === produtoId);
    setItemForm(f => ({
      ...f,
      produtoId,
      precoM2: prod ? String(prod.precoM2) : '',
      precoInstalacao: prod ? String(prod.precoInstalacao) : '',
    }));
  }

  function submitItem() {
    const data = {
      ...itemForm,
      larguraCm: parseFloat(itemForm.larguraCm),
      alturaCm: parseFloat(itemForm.alturaCm),
      quantidade: parseInt(itemForm.quantidade),
      precoM2: parseFloat(itemForm.precoM2),
      precoInstalacao: parseFloat(itemForm.precoInstalacao || '0'),
      produtoId: itemForm.produtoId || null,
      bando: itemForm.bando === 'sim',
      ladoComando: itemForm.ladoComando || null,
    };
    if (editingItemId) {
      updateItemMutation.mutate({ itemId: editingItemId, data });
    } else {
      addItemMutation.mutate(data);
    }
  }

  function startEditItem(item: any) {
    setItemForm({
      ambiente: item.ambiente,
      descricaoJanela: item.descricaoJanela || '',
      larguraCm: String(item.larguraCm),
      alturaCm: String(item.alturaCm),
      quantidade: String(item.quantidade),
      produtoId: item.produtoId || '',
      tipoInstalacao: item.tipoInstalacao,
      acionamento: item.acionamento,
      precoM2: String(item.produto?.precoM2 || ''),
      precoInstalacao: String(item.precoInstalacao),
      bando: item.bando ? 'sim' : 'nao',
      ladoComando: item.ladoComando || 'DIREITA',
      observacoes: item.observacoes || '',
    });
    setEditingItemId(item.id);
    setShowItemForm(true);
  }

  async function handleCameraCapture(dataUrl: string) {
    if (!cameraForItemId) return;
    setUploadingFoto(cameraForItemId);
    setShowCamera(false);
    try {
      const blob = await fetch(dataUrl).then(r => r.blob());
      const formData = new FormData();
      formData.append('foto', blob, 'foto.jpg');
      const res = await api.post('/upload/foto', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await api.post(`/orcamentos/itens/${cameraForItemId}/fotos`, { url: res.data.url });
      qc.invalidateQueries({ queryKey: ['orcamento', id] });
    } finally {
      setUploadingFoto(null);
      setCameraForItemId(null);
    }
  }

  async function handleFileUpload(itemId: string, file: File) {
    setUploadingFoto(itemId);
    try {
      const formData = new FormData();
      formData.append('foto', file);
      const res = await api.post('/upload/foto', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await api.post(`/orcamentos/itens/${itemId}/fotos`, { url: res.data.url });
      qc.invalidateQueries({ queryKey: ['orcamento', id] });
    } finally {
      setUploadingFoto(null);
    }
  }

  if (isLoading) return <div className="text-center py-12 text-gray-400">Carregando...</div>;
  if (!orcamento) return <div className="text-center py-12 text-gray-400">Orçamento não encontrado</div>;

  const areaM2 = (l: number, a: number) => (l / 100) * (a / 100);

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {showCamera && cameraForItemId && (
        <CameraCapture onCapture={handleCameraCapture} onClose={() => { setShowCamera(false); setCameraForItemId(null); }} />
      )}

      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/orcamentos')} className="text-gray-500 hover:text-gray-700">←</button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold">{orcamento.numero}</h1>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium badge-${orcamento.status.toLowerCase()}`}>
              {STATUS_LABELS[orcamento.status]}
            </span>
          </div>
          <p className="text-sm text-gray-500">{orcamento.cliente?.nome}</p>
        </div>
        <button
          onClick={handleDeleteOrcamento}
          disabled={deleteOrcamentoMutation.isPending}
          className="text-red-500 hover:text-red-600 text-sm"
          title="Excluir orçamento"
        >
          🗑️
        </button>
        <div className="relative">
          <button
            onClick={() => setShowPdfMenu(v => !v)}
            disabled={exportingPdf}
            className="btn-secondary text-sm flex items-center gap-1.5"
          >
            {exportingPdf ? 'Gerando...' : 'Exportar PDF'}
            {!exportingPdf && <span className="text-xs opacity-60">▾</span>}
          </button>
          {showPdfMenu && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setShowPdfMenu(false)} />
              <div className="absolute right-0 top-full mt-1 z-40 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[180px] overflow-hidden">
                <button
                  onClick={() => handleExportPdf('interno')}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex flex-col gap-0.5"
                >
                  <span className="font-medium">Documento Interno</span>
                  <span className="text-xs text-gray-400">Com preços detalhados</span>
                </button>
                <div className="border-t border-gray-100" />
                <button
                  onClick={() => handleExportPdf('cliente')}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex flex-col gap-0.5"
                >
                  <span className="font-medium">Documento Cliente</span>
                  <span className="text-xs text-gray-400">Sem preço/m², instalação e medidas</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Info do cliente */}
      <div className="card space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Informações</h2>
          {!editingInfo && (
            <button onClick={startEditInfo} className="text-sm text-blue-500">Editar</button>
          )}
        </div>

        {editingInfo ? (
          <div className="space-y-3 pt-1">
            <div>
              <label className="label">Data da Visita</label>
              <input type="datetime-local" className="input" value={infoForm.dataVisita} onChange={e => setInfoForm({...infoForm, dataVisita: e.target.value})} />
            </div>
            <div>
              <label className="label">Validade do Orçamento</label>
              <input type="date" className="input" value={infoForm.dataValidade} onChange={e => setInfoForm({...infoForm, dataValidade: e.target.value})} />
            </div>
            <div>
              <label className="label">Observações Gerais</label>
              <textarea className="input resize-none" rows={2} value={infoForm.observacoesGerais} onChange={e => setInfoForm({...infoForm, observacoesGerais: e.target.value})} />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setEditingInfo(false)} className="btn-secondary flex-1">Cancelar</button>
              <button
                onClick={() => updateOrcamentoMutation.mutate({
                  dataVisita: infoForm.dataVisita ? new Date(infoForm.dataVisita).toISOString() : null,
                  dataValidade: infoForm.dataValidade ? new Date(infoForm.dataValidade).toISOString() : null,
                  observacoesGerais: infoForm.observacoesGerais,
                })}
                disabled={updateOrcamentoMutation.isPending}
                className="btn-primary flex-1"
              >
                {updateOrcamentoMutation.isPending ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <div><span className="text-gray-500">Cliente:</span> <span className="font-medium">{orcamento.cliente?.nome}</span></div>
            <div><span className="text-gray-500">Tel:</span> {orcamento.cliente?.telefone}</div>
            {orcamento.dataVisita && (
              <div className="col-span-2"><span className="text-gray-500">Visita:</span> {new Date(orcamento.dataVisita).toLocaleString('pt-BR')}</div>
            )}
            {orcamento.dataValidade && (
              <div><span className="text-gray-500">Validade:</span> {new Date(orcamento.dataValidade).toLocaleDateString('pt-BR')}</div>
            )}
            {orcamento.observacoesGerais && (
              <div className="col-span-2"><span className="text-gray-500">Obs:</span> {orcamento.observacoesGerais}</div>
            )}
            <div><span className="text-gray-500">Responsável:</span> {orcamento.responsavel?.nome}</div>
          </div>
        )}

        {!editingInfo && (
          <div>
            <label className="label mt-2">Status</label>
            <select
              className="input"
              value={orcamento.status}
              onChange={e => updateStatusMutation.mutate(e.target.value)}
            >
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Itens / Janelas */}
      <div className="card space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Janelas / Ambientes</h2>
          <button onClick={() => { resetItemForm(); setEditingItemId(null); setShowItemForm(true); }} className="btn-primary text-sm py-1.5 px-3">
            + Adicionar
          </button>
        </div>

        {orcamento.itens?.length === 0 && (
          <p className="text-center py-4 text-gray-400 text-sm">Nenhuma janela adicionada ainda</p>
        )}

        {orcamento.itens?.map((item: any) => (
          <div key={item.id} className="border border-gray-200 rounded-xl p-3 space-y-2">
            <div className="flex items-start justify-between">
              <div>
                <span className="font-medium">{item.ambiente}</span>
                {item.descricaoJanela && <span className="text-gray-500 text-sm ml-2">– {item.descricaoJanela}</span>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => startEditItem(item)} className="text-blue-500 text-sm">✏️</button>
                <button onClick={() => deleteItemMutation.mutate(item.id)} className="text-red-500 text-sm">🗑️</button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 text-xs text-gray-600">
              <div><span className="text-gray-400">Medida</span><br />{item.larguraCm}×{item.alturaCm}cm</div>
              <div><span className="text-gray-400">Área</span><br />{areaM2(item.larguraCm, item.alturaCm).toFixed(2)}m²</div>
              <div><span className="text-gray-400">Qtd</span><br />{item.quantidade}x</div>
            </div>

            {item.produto && (
              <div className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                {item.produto.tipoPersiana?.nome} · {item.produto.marca} {item.produto.modelo} · {item.produto.cor}
              </div>
            )}

            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Subtotal</span>
              <span className="font-semibold text-primary-700">
                R$ {item.subtotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </span>
            </div>

            {/* Fotos */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-500 font-medium">Fotos ({item.fotos?.length || 0})</span>
                <button
                  onClick={() => { setCameraForItemId(item.id); setShowCamera(true); }}
                  className="text-xs bg-gray-100 px-2 py-1 rounded-lg hover:bg-gray-200"
                >
                  📷 Câmera
                </button>
                <label className="text-xs bg-gray-100 px-2 py-1 rounded-lg hover:bg-gray-200 cursor-pointer">
                  🖼️ Galeria
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={e => e.target.files?.[0] && handleFileUpload(item.id, e.target.files[0])}
                  />
                </label>
                {uploadingFoto === item.id && <span className="text-xs text-gray-400">Enviando...</span>}
              </div>

              {item.fotos?.length > 0 && (
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {item.fotos.map((foto: any) => (
                    <div key={foto.id} className="relative flex-shrink-0">
                      <img
                        src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || ''}${foto.url}`}
                        alt={foto.descricao || 'Foto'}
                        className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        onClick={() => deleteFotoMutation.mutate(foto.id)}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Totais */}
      {orcamento.totalBruto > 0 && (
        <div className="card space-y-2">
          <h2 className="font-semibold">Resumo Financeiro</h2>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Total bruto</span>
            <span>R$ {orcamento.totalBruto.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
          </div>
          {orcamento.descontoPercent > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Desconto ({orcamento.descontoPercent}%)</span>
              <span className="text-red-600">- R$ {((orcamento.totalBruto * orcamento.descontoPercent) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-lg border-t pt-2">
            <span>Total</span>
            <span className="text-primary-700">R$ {orcamento.totalLiquido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
          </div>

          <div className="flex items-center gap-2 mt-2">
            <label className="label mb-0 whitespace-nowrap">Desconto %</label>
            <input
              type="number"
              className="input"
              min="0"
              max="100"
              key={orcamento.descontoPercent}
              defaultValue={orcamento.descontoPercent ?? 0}
              onBlur={e => {
                const val = parseFloat(e.target.value) || 0;
                if (val !== (orcamento.descontoPercent ?? 0)) {
                  updateOrcamentoMutation.mutate({ descontoPercent: val });
                }
              }}
            />
          </div>
        </div>
      )}

      {/* Form de item */}
      {showItemForm && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-end md:items-center justify-center p-0 md:p-4">
          <div className="bg-white rounded-t-2xl md:rounded-2xl w-full md:max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold">{editingItemId ? 'Editar Janela' : 'Nova Janela'}</h3>
              <button onClick={() => { setShowItemForm(false); setEditingItemId(null); }}>✕</button>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="label">Ambiente *</label>
                  <input className="input" placeholder="Ex: Sala, Quarto 1, Cozinha" value={itemForm.ambiente} onChange={e => setItemForm({...itemForm, ambiente: e.target.value})} />
                </div>
                <div className="col-span-2">
                  <label className="label">Descrição da janela</label>
                  <input className="input" placeholder="Ex: Janela lateral, Porta de vidro" value={itemForm.descricaoJanela} onChange={e => setItemForm({...itemForm, descricaoJanela: e.target.value})} />
                </div>
                <div>
                  <label className="label">Largura (cm) *</label>
                  <input type="number" className="input" placeholder="150" value={itemForm.larguraCm} onChange={e => setItemForm({...itemForm, larguraCm: e.target.value})} />
                </div>
                <div>
                  <label className="label">Altura (cm) *</label>
                  <input type="number" className="input" placeholder="200" value={itemForm.alturaCm} onChange={e => setItemForm({...itemForm, alturaCm: e.target.value})} />
                </div>
                <div>
                  <label className="label">Quantidade</label>
                  <input type="number" className="input" min="1" value={itemForm.quantidade} onChange={e => setItemForm({...itemForm, quantidade: e.target.value})} />
                </div>
                <div>
                  <label className="label">Tipo de instalação</label>
                  <select className="input" value={itemForm.tipoInstalacao} onChange={e => setItemForm({...itemForm, tipoInstalacao: e.target.value})}>
                    {TIPO_INSTALACAO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="label">Produto / Persiana</label>
                  <select className="input" value={itemForm.produtoId} onChange={e => handleProdutoChange(e.target.value)}>
                    <option value="">-- Selecionar produto --</option>
                    {produtos.map((p: any) => (
                      <option key={p.id} value={p.id}>
                        {p.tipoPersiana?.nome} · {p.marca} {p.modelo} · {p.cor}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Preço por m² (R$) *</label>
                  <input type="number" step="0.01" className="input" value={itemForm.precoM2} onChange={e => setItemForm({...itemForm, precoM2: e.target.value})} />
                </div>
                <div>
                  <label className="label">Instalação (R$)</label>
                  <input type="number" step="0.01" className="input" value={itemForm.precoInstalacao} onChange={e => setItemForm({...itemForm, precoInstalacao: e.target.value})} />
                </div>
                <div className="col-span-2">
                  <label className="label">Bandô</label>
                  <select className="input" value={itemForm.bando} onChange={e => setItemForm({...itemForm, bando: e.target.value})}>
                    <option value="sim">Sim</option>
                    <option value="nao">Não</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="label">Acionamento</label>
                  <select className="input" value={itemForm.acionamento} onChange={e => setItemForm({...itemForm, acionamento: e.target.value})}>
                    {ACIONAMENTO.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="label">Lado comando</label>
                  <select className="input" value={itemForm.ladoComando} onChange={e => setItemForm({...itemForm, ladoComando: e.target.value})}>
                    {LADO_COMANDO.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </select>
                </div>

                {itemForm.larguraCm && itemForm.alturaCm && itemForm.precoM2 && (
                  <div className="col-span-2 bg-primary-50 p-3 rounded-lg text-sm">
                    <div className="text-gray-600">Área: {areaM2(parseFloat(itemForm.larguraCm), parseFloat(itemForm.alturaCm)).toFixed(2)} m²</div>
                    <div className="font-bold text-primary-700">
                      Subtotal estimado: R$ {(
                        (areaM2(parseFloat(itemForm.larguraCm), parseFloat(itemForm.alturaCm)) * parseFloat(itemForm.precoM2) +
                        parseFloat(itemForm.precoInstalacao || '0')) * parseInt(itemForm.quantidade || '1')
                      ).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                )}

                <div className="col-span-2">
                  <label className="label">Observações</label>
                  <textarea className="input resize-none" rows={2} value={itemForm.observacoes} onChange={e => setItemForm({...itemForm, observacoes: e.target.value})} />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={() => { setShowItemForm(false); setEditingItemId(null); }} className="btn-secondary flex-1">Cancelar</button>
                <button
                  onClick={submitItem}
                  disabled={!itemForm.ambiente || !itemForm.larguraCm || !itemForm.alturaCm || !itemForm.precoM2}
                  className="btn-primary flex-1"
                >
                  {editingItemId ? 'Salvar' : 'Adicionar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
